import { Leaf, Users, Sprout, Heart, MessageCircle, TreePine, BookOpen, Wheat, Handshake, Gamepad2, Orbit } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";

const MANIFESTO = [
  {
    icon: MessageCircle,
    title: "Comunicazione Educativa",
    text: "Riconosciamo l'importanza della comunicazione educativa come strumento fondamentale per generare cambiamenti positivi nella società.",
  },
  {
    icon: TreePine,
    title: "Outdoor Education",
    text: "Crediamo fermamente nell'Outdoor Education come occasione imprescindibile per la crescita e lo sviluppo integrale dell'individuo, promuovendo l'apprendimento attraverso esperienze dirette e immersive nella natura.",
  },
  {
    icon: BookOpen,
    title: "Ricerca Pedagogica",
    text: "Proponiamo attività educative basate su una ricerca pedagogica approfondita e costante, al fine di garantire esperienze di alta qualità e in continua evoluzione.",
  },
  {
    icon: Orbit,
    title: "Visione Olistica della Natura",
    text: "Sosteniamo una visione olistica della natura, dove tutto è interconnesso e le relazioni tra i viventi hanno tutte la stessa dignità, promuovendo il rispetto e la cura per l'ambiente e tutte le forme di vita.",
  },
  {
    icon: Wheat,
    title: "Cultura Rurale e Sapere Materiale",
    text: "Crediamo che i saperi materiali e la cultura rurale siano un bagaglio indispensabile per affrontare le sfide del futuro, valorizziamo le tradizioni e il patrimonio culturale legato alla vita e alla lavorazione della terra.",
  },
  {
    icon: Handshake,
    title: "Valore delle Relazioni",
    text: "Crediamo nel valore delle relazioni umane e nel potere dello scambio di esperienze, promuovendo la costruzione di comunità solidali e inclusive.",
  },
  {
    icon: Gamepad2,
    title: "Metodologia Ludica",
    text: "Promuoviamo la metodologia ludica come strumento educativo per stimolare la curiosità, l'esplorazione e la collaborazione tra i partecipanti, favorendo un apprendimento attivo e partecipativo.",
  },
];

export default function About() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location]);
  return (
    <div className="bg-[#FBF9F6]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=2000" alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-br from-[#4A6B53]/85 to-[#2C312E]/70"/>
        </div>
        <div className="relative mx-auto max-w-5xl px-6 md:px-10 py-24 md:py-36">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#E4B871] mb-4">Il Network</div>
          <h1 className="font-display font-black text-4xl md:text-6xl text-white leading-tight max-w-3xl">
            Un'associazione di persone che credono nella forza educativa della natura.
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 md:px-10 py-20 md:py-28 space-y-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Leaf, t: "Outdoor Education", d: "Apprendere all'aria aperta, con le mani nella terra e gli occhi aperti sul mondo." },
            { icon: Users, t: "Comunità", d: "Una rete di fattorie, educatori, famiglie e scuole che si sostengono a vicenda." },
            { icon: Sprout, t: "Crescita", d: "Laboratori, campi estivi, progetti pedagogici, pic-nic e feste in campagna." },
          ].map(({ icon: Icon, t, d }, i) => (
            <div key={i} className="rounded-3xl bg-white border border-[#E5DCD0] p-8 rise" style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="w-12 h-12 rounded-2xl bg-[#F2EBE1] text-[#4A6B53] flex items-center justify-center mb-4"><Icon size={22}/></div>
              <div className="font-display font-extrabold text-xl text-[#2C312E]">{t}</div>
              <p className="mt-2 text-[#58625B] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-10 items-center">
          <img src="https://images.pexels.com/photos/32608378/pexels-photo-32608378.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="" className="rounded-[2rem] w-full object-cover h-[420px]"/>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C8795A] mb-3">La nostra missione</div>
            <h2 className="font-display font-black text-3xl md:text-4xl text-[#2C312E] leading-tight">
              Riportare i bambini in natura, una fattoria alla volta.
            </h2>
            <p className="mt-6 text-[#58625B] leading-relaxed">
              Il Network Agrigiochiamo riunisce aziende agricole, fattorie didattiche, cooperative sociali e
              realtà educative distribuite in tutta Italia. Lavoriamo in gruppi tematici: rappresentanza e rapporti
              con gli enti, sviluppo della rete associativa, comunicazione, progetti e fundraising, ricerca
              psicologica e pedagogica.
            </p>
            <p className="mt-4 text-[#58625B] leading-relaxed">
              Crediamo che crescere a contatto con animali, piante e persone che sanno ascoltare la terra
              sia una delle esperienze più preziose che possiamo offrire alle nuove generazioni.
            </p>
            <Link to="/fattorie" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white px-6 py-3 text-sm font-bold hover:bg-[#3D5844] hover:-translate-y-0.5 transition-all">
              Scopri le fattorie del Network
            </Link>
          </div>
        </div>

        <div className="rounded-[2rem] bg-[#4A6B53] text-white p-10 md:p-14 relative overflow-hidden">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#E4B871] mb-3">Campagna 2026</div>
            <h3 className="font-display font-black text-3xl md:text-4xl leading-tight">
              Sei una fattoria o un educatore?<br/>Unisciti al Network.
            </h3>
            <p className="mt-4 text-white/85 leading-relaxed">
              Quota associativa di €120 per i nuovi soci, €100 per il rinnovo. Accesso a gruppi di lavoro,
              progetti, formazione, kit associativo e visibilità sulla piattaforma.
            </p>
            <Link to="/adesione" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#E4B871] text-[#2C312E] px-6 py-3 text-sm font-bold hover:-translate-y-0.5 transition-all" data-testid="about-join-cta">
              Iscriviti ora
            </Link>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="bg-[#F2EBE1] py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C8795A] mb-3">
              Il nostro Manifesto
            </div>
            <h2 className="font-display font-black text-3xl md:text-5xl text-[#2C312E] leading-tight">
              Sette principi<br/>che ci guidano ogni giorno.
            </h2>
            <p className="mt-5 text-[#58625B] leading-relaxed text-lg">
              Noi, le realtà che aderiscono al Network Agrigiochiamo, ci impegniamo a seguire
              e promuovere i seguenti principi.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {MANIFESTO.map((p, i) => {
              const Icon = p.icon;
              const featured = i === 3; // "Visione Olistica" posta come card in evidenza
              return (
                <article
                  key={p.title}
                  className={`rounded-[2rem] p-7 md:p-8 rise border transition-all ${
                    featured
                      ? "bg-[#2C312E] text-white border-[#2C312E] lg:col-span-1 md:row-span-1 hover:shadow-xl"
                      : "bg-white border-[#E5DCD0] hover:border-[#C8795A]/40 hover:shadow-lg"
                  }`}
                  style={{ animationDelay: `${i * 0.05}s` }}
                  data-testid={`manifesto-${i}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                      featured ? "bg-[#E4B871] text-[#2C312E]" : "bg-[#F2EBE1] text-[#4A6B53]"
                    }`}>
                      <Icon size={22} strokeWidth={2.2}/>
                    </div>
                    <span className={`font-display font-black text-5xl leading-none ${
                      featured ? "text-white/15" : "text-[#E5DCD0]"
                    }`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className={`font-display font-extrabold text-xl leading-tight ${
                    featured ? "text-white" : "text-[#2C312E]"
                  }`}>
                    {p.title}
                  </h3>
                  <p className={`mt-3 leading-relaxed text-sm ${
                    featured ? "text-white/80" : "text-[#58625B]"
                  }`}>
                    {p.text}
                  </p>
                </article>
              );
            })}
          </div>

          <div className="mt-14 rounded-[2rem] bg-white border border-[#E5DCD0] p-8 md:p-12 text-center max-w-3xl mx-auto">
            <p className="font-display italic text-xl md:text-2xl text-[#2C312E] leading-relaxed">
              « Con questo manifesto, ci impegniamo a lavorare con dedizione e responsabilità
              per contribuire alla crescita individuale e collettiva, promuovendo valori
              di sostenibilità, inclusione e rispetto per l'ambiente e per le persone. »
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
